import { acquireAudioSession } from './AudioSession';

// Trim only a genuinely quiet prefix, preserving 30 ms before a sustained
// onset. -60 dB is deliberately conservative so quiet speech is not cut off.
export const leadingSilenceOffset = (buffer) => {
    const channels = Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i));
    const windowSize = Math.max(1, Math.round(buffer.sampleRate * 0.005));
    let first = null;
    for (let start = 0; start < buffer.length; start += windowSize) {
        const end = Math.min(start + windowSize, buffer.length);
        let power = 0;
        for (const channel of channels) for (let i = start; i < end; i++) power += channel[i] ** 2;
        if (Math.sqrt(power / ((end - start) * channels.length)) >= 0.001) {
            if (first !== null) {
                const offset = first / buffer.sampleRate;
                return offset >= 0.1 ? Math.max(0, offset - 0.03) : 0;
            }
            first = start;
        } else first = null;
    }
    return 0; // Silent files and clips with no sustained onset stay untouched.
};

export const createGameAudioPlayer = (urls) => {
    const Context = window.AudioContext || window.webkitAudioContext;
    let context;
    try { context = Context ? new Context() : null; } catch { context = null; }
    const decoded = new Map();
    const fallback = new Map();
    const active = new Map();
    const activeFallback = new Map();
    let generation = 0;
    let disposed = false;
    let releaseSession;

    const fallbackFor = (url) => {
        if (!fallback.has(url)) {
            const audio = new Audio(url);
            audio.preload = 'auto';
            fallback.set(url, audio);
        }
        return fallback.get(url);
    };
    const load = (url) => {
        if (!context) { fallbackFor(url); return Promise.resolve(null); }
        if (!decoded.has(url)) decoded.set(url, (async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error('Unable to load game sound.');
                const buffer = await context.decodeAudioData(await response.arrayBuffer());
                return { buffer, offset: leadingSilenceOffset(buffer) };
            } catch {
                // Preserve playback when Web Audio cannot decode a browser codec.
                if (!disposed) fallbackFor(url);
                return null;
            }
        })());
        return decoded.get(url);
    };
    const stop = () => {
        generation++;
        for (const [source, finish] of active) { try { source.stop(); } catch { /* Already ended. */ } finish(); }
        active.clear();
        for (const audio of fallback.values()) {
            audio.pause();
            activeFallback.get(audio)?.();
        }
        activeFallback.clear();
    };
    const recoverInterruptedContext = () => {
        if (!Context || (context && context.state !== 'closed' && context.state !== 'interrupted')) return;
        const previous = context;
        stop();
        try { context = new Context(); } catch { context = previous; }
        if (context !== previous && previous?.state !== 'closed') previous?.close()?.catch(() => {});
    };
    const playInternal = async (url, waitForEnd, loop = false) => {
        if (disposed) return;
        const version = generation;
        const clip = await load(url);
        if (disposed || version !== generation) return;
        if (clip) {
            try {
                if (context.state !== 'running' && context.state !== 'closed') await context.resume();
                if (disposed || version !== generation) return;
                // Safari can resolve resume() while the context remains suspended.
                // In that case HTMLAudio is more reliable than silently playing nothing.
                if (context.state === 'running') {
                    const source = context.createBufferSource();
                    source.buffer = clip.buffer;
                    source.loop = loop;
                    source.connect(context.destination);
                    let settled = false;
                    let resolveEnded;
                    const ended = new Promise(resolve => { resolveEnded = resolve; });
                    const finish = () => {
                        if (settled) return;
                        settled = true;
                        active.delete(source);
                        try { source.disconnect(); } catch { /* Already disconnected. */ }
                        resolveEnded();
                    };
                    source.onended = finish;
                    active.set(source, finish);
                    source.start(0, clip.offset);
                    if (waitForEnd) await ended;
                    return;
                }
            } catch {
                // Fall through to HTMLAudio when Web Audio cannot start.
            }
        }

        const audio = fallbackFor(url);
        audio.pause();
        activeFallback.get(audio)?.();
        audio.currentTime = 0;
        audio.loop = loop;
        let settled = false;
        let resolveEnded;
        const ended = new Promise(resolve => { resolveEnded = resolve; });
        const finish = () => {
            if (settled) return;
            settled = true;
            activeFallback.delete(audio);
            audio.removeEventListener?.('ended', finish);
            resolveEnded();
        };
        activeFallback.set(audio, finish);
        audio.addEventListener?.('ended', finish, { once: true });
        // Some light-weight test/browser audio shims expose only onended.
        if (!audio.addEventListener) audio.onended = finish;
        try { await audio.play(); } catch (error) { finish(); throw error; }
        if (waitForEnd) await ended;
    };
    return {
        preload: () => Promise.all(urls.map(load)),
        unlock() {
            if (disposed) return Promise.resolve();
            releaseSession ??= acquireAudioSession();
            // Rebuild a stuck iOS context inside a real gesture, reusing buffers.
            recoverInterruptedContext();
            const promises = [];
            if (context && context.state !== 'closed') {
                // Start/resume before an await loses iOS gesture activation.
                if (context.state !== 'running') promises.push(context.resume());
                if (context.createBuffer) {
                    const source = context.createBufferSource();
                    source.buffer = context.createBuffer(1, 1, context.sampleRate || 44100);
                    source.connect(context.destination);
                    source.onended = () => source.disconnect();
                    source.start(0);
                }
            }
            for (const audio of fallback.values()) {
                if (!audio.paused) continue;
                audio.muted = true;
                const attempt = Promise.resolve(audio.play()).catch(() => {}).finally(() => {
                    audio.pause(); audio.currentTime = 0; audio.muted = false;
                });
                promises.push(attempt);
            }
            return Promise.allSettled(promises);
        },
        resume() {
            if (disposed) return Promise.resolve();
            releaseSession ??= acquireAudioSession();
            recoverInterruptedContext();
            const promises = [];
            if (context && context.state !== 'running' && context.state !== 'closed') {
                promises.push(Promise.resolve(context.resume()).catch(() => {}));
            }
            // Mobile browsers may pause an HTMLAudio fallback while backgrounded.
            for (const audio of activeFallback.keys()) {
                if (audio.paused) promises.push(Promise.resolve(audio.play()).catch(() => {}));
            }
            return Promise.allSettled(promises);
        },
        play: url => playInternal(url, false),
        playUntilEnded: url => playInternal(url, true),
        playLoop: url => playInternal(url, false, true),
        stop,
        dispose() {
            releaseSession?.(); releaseSession = null;
            disposed = true; stop(); decoded.clear();
            for (const audio of fallback.values()) { audio.removeAttribute('src'); audio.load(); }
            fallback.clear();
            if (context && context.state !== 'closed') context.close().catch(() => {});
        },
    };
};
